import { action, computed, observable, makeObservable } from 'mobx';
import { sanitizeUrl } from '@braintree/sanitize-url';

import {
  ACCOUNT_SETTINGS_PATH,
  INSTORE_CART_PATH,
  ConnectedBankAccountDetails,
  EnrollmentSteps,
  LOG_NAMESPACES,
} from 'src/payments/constants';
import { ConnectionStatus, StatusTypes } from 'types/graphql';
import { Nullish } from 'shared/utils/type-utils';
import { InterfaceVariants } from 'src/constants/interface-variants';
import FeatureFlagsStore from 'src/state/feature-flags';
import { getLogger } from 'src/utils/datadog-logger';
import { removePersistedValue } from 'shared/utils/persisted-values';
import { extractRefUrlFromCurrentUrl } from 'src/payments/utils/parse-redirect-url';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

export type AddressProps = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
};

export type Aggregator = 'plaid';

export type EnrollmentBankAccount = {
  accountAlias: string;
  aggregator: Aggregator;
  bankName: string;
  billingAddressId: string;
  connectionStatus: ConnectionStatus;
  id: string;
  lastDigits: string;
  status: string;
  financialInstitutionLogo?: string | null;
};

export type EnrollmentStatusReceivedProps = {
  enrollmentStatus: StatusTypes;
  relinkOrEditBankAccountEnabled?: boolean;
  completeEnrollment?: boolean;
};
const initializedDispensary = {
  brandedDutchiePayEnabled: false,
  brandedDutchiePayLogoDark: '',
  brandedDutchiePayLogoLight: '',
  brandedDutchiePayName: '',
  id: '',
  logo: '',
  name: '',
  useWhiteLabel: false,
};

export default class DutchiePayEnrollment {
  @observable featureFlags: FeatureFlagsStore;
  @observable activateBankAccount = false;
  @observable address: AddressProps | Nullish | Record<string, never> = {};
  @observable aggregatorLinkLoading = false;
  @observable aggregatorLinkCompleted = false;
  @observable connectedBankAccountDetails: ConnectedBankAccountDetails | null = null;
  @observable currentStep: EnrollmentSteps | '' = '';
  @observable isExistingUser = false;
  @observable isMobileEcommSignup = false;
  @observable mxBillingAddressId = '';
  @observable refUrl = '';
  @observable userAcceptance = false;
  @observable userEmail = '';
  @observable autoCheckoutAfterBankLinkReconnect = false;
  @observable entryPoint = '';
  @observable dispensary = initializedDispensary;

  constructor(featureFlagsStore: FeatureFlagsStore) {
    makeObservable(this);
    this.featureFlags = featureFlagsStore;
  }

  // eslint-disable-next-line @typescript-eslint/unbound-method
  @action.bound
  setCurrentStep(step: EnrollmentSteps): void {
    this.currentStep = step;
    if (step === EnrollmentSteps.completeEnrollment || step === EnrollmentSteps.alreadySignedUp) {
      this.clearModalEnrollment();
    }
  }

  @action
  setDispensary(dispensary, uiVariant?, isReset = false): void {
    const { embedded, plus } = InterfaceVariants;
    if (!dispensary) {
      this.dispensary = initializedDispensary;

      if (!isReset) {
        logger.error(`Error in DutchiePayEnrollment.setDispensary. Dispensary is undefined`, {
          methodName: 'setDispensary',
          uiVariant,
          storeState: {
            currentStep: this.currentStep,
            refUrl: this.refUrl,
            entryPoint: this.entryPoint,
            dispensaryName: this.dispensary.name,
            dispensaryId: this.dispensary.id,
            useWhiteLabel: this.dispensary.useWhiteLabel,
          },
        });
      }
    } else {
      const hasWhiteLabelAssets =
        !!dispensary?.brandedDutchiePayName &&
        !!dispensary?.brandedDutchiePayLogoLight &&
        !!dispensary?.brandedDutchiePayLogoDark;

      const isWhiteLabeled = Boolean(
        [embedded, plus].includes(uiVariant) && dispensary.brandedDutchiePayEnabled && hasWhiteLabelAssets
      );

      this.dispensary = {
        brandedDutchiePayEnabled: dispensary.brandedDutchiePayEnabled,
        brandedDutchiePayLogoDark: isWhiteLabeled ? dispensary?.brandedDutchiePayLogoDark : '',
        brandedDutchiePayLogoLight: isWhiteLabeled ? dispensary?.brandedDutchiePayLogoLight : '',
        brandedDutchiePayName: isWhiteLabeled ? dispensary?.brandedDutchiePayName : '',
        id: dispensary?.id,
        logo: dispensary?.SpecialLogoImage,
        name: dispensary?.name,
        useWhiteLabel: isWhiteLabeled,
      };
    }
  }

  @action
  resetEnrollment(): void {
    this.activateBankAccount = false;
    this.address = {};
    this.currentStep = '';
    this.isExistingUser = false;
    this.userAcceptance = false;
    this.userEmail = '';
    this.autoCheckoutAfterBankLinkReconnect = false;
    this.entryPoint = '';
    this.setDispensary(null, undefined, true);
    this.setRefUrl(this.refUrl);
  }

  @action
  cancelEnrollment(): void {
    this.resetEnrollment();
    this.clearModalEnrollment();
  }

  @action.bound
  enrollmentStatusReceived({
    enrollmentStatus,
    relinkOrEditBankAccountEnabled = false,
    completeEnrollment = false,
  }: EnrollmentStatusReceivedProps): void {
    // This accounts for the situation where a users enrollment status is received from the queryMe call after SSO login
    // prior to the signup page being loaded
    const extractedRefUrl = extractRefUrlFromCurrentUrl();
    // Update the refUrl if it is not already set and the extractedRefUrl is present
    if (!this.refUrl && !!extractedRefUrl) {
      this.setRefUrl(extractedRefUrl);
    }

    if (enrollmentStatus === StatusTypes.statusEnrolled && !relinkOrEditBankAccountEnabled) {
      if (completeEnrollment && !this.refUrl) {
        this.currentStep = EnrollmentSteps.success;
      } else {
        this.currentStep = completeEnrollment ? EnrollmentSteps.completeEnrollment : EnrollmentSteps.alreadySignedUp;
      }
    }
  }
  @action.bound
  userAlreadySignedUp(): void {
    this.currentStep = EnrollmentSteps.alreadySignedUp;
  }

  @action.bound
  plaidOauthReceived(): void {
    this.currentStep = EnrollmentSteps.connectBank;
  }

  @computed
  get isCobrandedSignup(): boolean {
    const isAccountSettings = this.refUrl.includes(ACCOUNT_SETTINGS_PATH);
    return Boolean(this.dispensary.logo || this.dispensary.useWhiteLabel) && !isAccountSettings;
  }

  @computed
  get isInstoreSignup(): boolean {
    return this.refUrl.includes(INSTORE_CART_PATH);
  }

  @action
  setRefUrl(refUrl: string): void {
    if (refUrl === '') {
      this.refUrl = '';
      return;
    }
    const sanitized = sanitizeUrl(refUrl);
    // Only allow relative paths to prevent open redirect attacks.
    // Must start with / but not // (protocol-relative URLs like //evil.com are also open redirects).
    const isRelativePath = sanitized.startsWith('/') && !sanitized.startsWith('//');
    this.refUrl = isRelativePath ? sanitized : '';
  }

  @action
  beginEnrollment(userExists, refUrl = ''): void {
    const savedEmailAddress = this.userEmail || '';
    this.resetEnrollment();
    this.userEmail = savedEmailAddress;
    this.setRefUrl(refUrl);
    if (savedEmailAddress) {
      this.emailStepCompleted(savedEmailAddress);
    }
    this.setIsMobileEcommSignup(refUrl.includes('mobile-ecomm'));

    if (this.currentStep === EnrollmentSteps.connectBank) {
      return;
    }

    if (userExists) {
      this.currentStep = EnrollmentSteps.connectBank;
      return;
    }

    if (this.currentStep !== EnrollmentSteps.signUpForm) {
      this.resetEnrollment();
      this.currentStep = EnrollmentSteps.emailForm;
    }
  }

  @action setExistingUser(status: boolean): void {
    this.isExistingUser = status;
  }

  @action
  emailStepCompleted(email: string): void {
    this.userEmail = email;
    this.currentStep = this.isExistingUser ? EnrollmentSteps.passwordForm : EnrollmentSteps.signUpForm;
  }

  @action
  passwordStepCompleted(isEnrolled: boolean): void {
    if (!isEnrolled) {
      this.currentStep = EnrollmentSteps.connectBank;
    }
  }

  @action
  signUpStepCompleted(): void {
    this.currentStep = EnrollmentSteps.connectBank;
  }

  @action
  reconnectBankAccount(): void {
    this.currentStep = EnrollmentSteps.connectBank;
  }

  @action
  setIsMobileEcommSignup(value: boolean): void {
    this.isMobileEcommSignup = value;
  }

  @action
  setAddress(address: AddressProps): void {
    this.address = address;
  }

  @action
  setAutoCheckoutAfterBankLinkReconnect(status: boolean): void {
    this.autoCheckoutAfterBankLinkReconnect = status;
  }

  @action
  createBankAccountRequested(): void {
    this.currentStep = EnrollmentSteps.tosLoading;
  }

  @action
  createBankAccountCompleted(): void {
    this.currentStep = EnrollmentSteps.terms;
  }

  @action.bound
  setConnectedBankAccountDetails(connectedBankAccount: ConnectedBankAccountDetails): void {
    this.connectedBankAccountDetails = connectedBankAccount;
  }

  @action.bound
  completeAggregatorLinkStarted(): void {
    this.setCurrentStep(EnrollmentSteps.bankAccountsLoading);
  }

  @action.bound
  completeAggregatorLinkSucceeded(connectedBankAccount: ConnectedBankAccountDetails): void {
    this.setConnectedBankAccountDetails(connectedBankAccount);
    this.setCurrentStep(EnrollmentSteps.terms);
  }

  @action.bound
  aggregatorLinkExited(): void {
    this.clearModalEnrollment();
    this.setCurrentStep(EnrollmentSteps.completeEnrollment);
  }

  @action
  termsOfServiceError(): void {
    this.currentStep = EnrollmentSteps.terms;
  }

  @action
  editBankAccount(): void {
    this.currentStep = EnrollmentSteps.connectBank;
  }

  @action
  userAcceptanceRequested(): void {
    this.currentStep = EnrollmentSteps.tosLoading;
  }

  @action
  activateBankAccountRequested(): void {
    this.currentStep = EnrollmentSteps.tosLoading;
  }

  @action resetPasswordClicked(): void {
    this.currentStep = EnrollmentSteps.forgotPasswordForm;
  }

  @action resetPasswordSucceeded(): void {
    this.currentStep = EnrollmentSteps.passwordForm;
  }

  @action clearModalEnrollment(): void {
    removePersistedValue('modalEnrollment', true);
    removePersistedValue('modalEnrollmentStep', true);
  }
}
